import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {IconButton, Stack, Tooltip} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'

import type {SimilarIssue} from './SimilarIssues'
import styles from './SimilarIssues.module.css'

type FeedbackRating = 'positive' | 'negative'

/** Tracks only the most recent feedback submission */
type LastSubmittedFeedback = {
  rating: FeedbackRating
  itemIds: Set<string>
}

type SimilarIssuesFeedbackProps = {
  items: SimilarIssue[]
  repoOwner: string
  repoName: string
  /** Session ID ref for correlating analytics events */
  sessionIdRef: React.RefObject<string>
}

function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

/**
 * Basic thumbs up/down feedback for similar issues results.
 *
 * - Only visible when results exist (but stays mounted to preserve state when results are cleared)
 * - After voting, buttons are disabled until results change
 * - On new results, only the opposite vote is enabled (switch allowed, not re-vote to reduce noise)
 * - Uses optimistic UI updates since voting is non-critical
 */
export function SimilarIssuesFeedback({items, repoOwner, repoName, sessionIdRef}: SimilarIssuesFeedbackProps) {
  const [lastSubmittedFeedback, setLastSubmittedFeedback] = useState<LastSubmittedFeedback | null>(null)

  const currentItemIds = useMemo(() => new Set(items.map(item => item.id)), [items])
  const resultsChangedSinceVote = useMemo(() => {
    if (!lastSubmittedFeedback) return false
    return !areSetsEqual(lastSubmittedFeedback.itemIds, currentItemIds)
  }, [lastSubmittedFeedback, currentItemIds])

  const isPositiveDisabled =
    lastSubmittedFeedback && (!resultsChangedSinceVote || lastSubmittedFeedback.rating === 'positive')
  const isNegativeDisabled =
    lastSubmittedFeedback && (!resultsChangedSinceVote || lastSubmittedFeedback.rating === 'negative')

  const handleFeedback = useCallback(
    (rating: FeedbackRating) => {
      if (rating === 'positive' && isPositiveDisabled) return
      if (rating === 'negative' && isNegativeDisabled) return

      // Optimistic update - don't block UI
      setLastSubmittedFeedback({
        rating,
        itemIds: new Set(items.map(item => item.id)),
      })
      verifiedFetchJSON(`/${repoOwner}/${repoName}/issues/semantically_similar/feedback`, {
        method: 'POST',
        body: {
          rating,
          session_id: sessionIdRef.current,
        },
        // eslint-disable-next-line github/no-then -- don't await here since voting is non-critical
      }).catch(() => {
        // Silent failures are acceptable because voting is non-critical
      })
    },
    [repoOwner, repoName, items, isPositiveDisabled, isNegativeDisabled, sessionIdRef],
  )

  // Hide when no items, but stay mounted to preserve state
  if (items.length === 0) {
    return null
  }

  const getPositiveTooltipDescription = (): string => {
    if (!lastSubmittedFeedback) return 'Positive feedback'
    if (lastSubmittedFeedback.rating === 'positive') return 'Positive feedback submitted'
    if (resultsChangedSinceVote) return 'Switch to positive feedback'
    return 'Vote locked until results change'
  }

  const getNegativeTooltipDescription = (): string => {
    if (!lastSubmittedFeedback) return 'Negative feedback'
    if (lastSubmittedFeedback.rating === 'negative') return 'Negative feedback submitted'
    if (resultsChangedSinceVote) return 'Switch to negative feedback'
    return 'Vote locked until results change'
  }

  return (
    <div className={styles.feedbackRow}>
      <Stack direction="horizontal" gap="condensed">
        <Tooltip text={getPositiveTooltipDescription()} direction="s">
          <IconButton
            aria-label="Positive feedback"
            icon={ThumbsupIcon}
            variant="invisible"
            size="small"
            onClick={() => handleFeedback('positive')}
            aria-disabled={isPositiveDisabled || undefined}
            className={lastSubmittedFeedback?.rating === 'positive' ? styles.submittedFeedbackButton : undefined}
          />
        </Tooltip>
        <Tooltip text={getNegativeTooltipDescription()} direction="s">
          <IconButton
            aria-label="Negative feedback"
            icon={ThumbsdownIcon}
            variant="invisible"
            size="small"
            onClick={() => handleFeedback('negative')}
            aria-disabled={isNegativeDisabled || undefined}
            className={lastSubmittedFeedback?.rating === 'negative' ? styles.submittedFeedbackButton : undefined}
          />
        </Tooltip>
      </Stack>
    </div>
  )
}
